import { Button, DefaultIconButton, FormItem, Heading, Switch, Tooltip } from '../Ant';
import TextAreaField from '../textAreaField/textAreaField';
import InfoMessage from '../dashboard/addTransaction/infoMessage';
import { InfoLinear } from '../icons';
import MatterRequestBusinessAreaSelect from './matterRequestBusinessAreaSelect';
import MatterRequestCurrencySelect from './matterRequestCurrencySelect';
import MatterRequestCustomerSelect from './matterRequestCustomerSelect';
import MatterRequestLanguageSelect from './matterRequestLanguageSelect';
import MatterRequestTypeSelect from './matterRequestTypeSelect';
import MatterRequestUserSelect from './matterRequestUserSelect';
import { IMatterRequest } from '../../interface/matterRequest';
import { useTranslation } from 'react-i18next';
import PinnedSaveButton from '../pinnedSaveButton/pinnedSaveButton';

interface DetailsFormProps {
  matterRequest: IMatterRequest | null;
  updateManageMatterRequest: (data: IMatterRequest | null, reset?: boolean) => void;
  handleSelectCustomer: () => void;
  handleSelectMatterRequestType: () => void;
  handleSelectLanguage: () => void;
  handleSelectCurrency: () => void;
  handleSelectOwner: () => void;
  handleSelectResponsible: () => void;
  handleSelectBusinessArea: () => void;
  onCreateMatterRequest: () => void;
  onEditMatterRequest: () => void;
  isFormDisabled: boolean;
  isEditing: boolean;
  getCustomerInputValue: () => string;
  isFromMailOrAppointment?: boolean;
  handleSwitchMailAsNote: (value: boolean) => void;
  isPeopleReadOnly?: boolean;
}

const DetailsForm: React.FC<DetailsFormProps> = ({
  matterRequest,
  updateManageMatterRequest,
  handleSelectCustomer,
  handleSelectMatterRequestType,
  handleSelectLanguage,
  handleSelectCurrency,
  handleSelectOwner,
  handleSelectResponsible,
  handleSelectBusinessArea,
  onCreateMatterRequest,
  onEditMatterRequest,
  isFormDisabled,
  isEditing,
  getCustomerInputValue,
  isFromMailOrAppointment,
  handleSwitchMailAsNote,
  isPeopleReadOnly = false,
}) => {
  const { t } = useTranslation();
  const isAppointment =
    Office.context?.mailbox?.item?.itemType === Office.MailboxEnums.ItemType.Appointment;

  const renderRecommendedLabel = (label: string) => (
    <span className="recommended-label" data-testid="recommended-label">
      {label}
      <span className="recommended-suffix">({t('common:recommended')})</span>
      <Tooltip title={t('matterRequest:recommendedFieldTooltip')} placement="top" arrow={false}>
        <DefaultIconButton
          type="text"
          className="recommended-info"
          data-testid="recommended-info"
          aria-label={t('matterRequest:recommendedFieldTooltip')}
          icon={<InfoLinear />}
        />
      </Tooltip>
    </span>
  );

  return (
    <>
      <FormItem label={t('customer:customer')} name="customer" required>
        <div className="relative">
          {matterRequest?.currentStepOption === 'new' ? (
            <Button
              type="default"
              data-testid="add-customer-button"
              data-field="customer"
              className="add-transaction-btn"
              block
              onClick={handleSelectCustomer}
            >
              <div className="d-flex w-full justify-between items-center selected-values">
                <Heading
                  level={6}
                  $fontsize="13px"
                  $color="title"
                  $weight={400}
                  $lineheight="20px"
                  className="ta-left lineclamp-1"
                  data-testid="selected-customer-name"
                >
                  {getCustomerInputValue()}
                </Heading>
              </div>
            </Button>
          ) : (
            <MatterRequestCustomerSelect onOpenPicker={handleSelectCustomer} />
          )}
        </div>
      </FormItem>
      <FormItem label={t('common:subject')} required>
        <TextAreaField
          data-testid="subject-input"
          value={matterRequest?.subject ?? ''}
          onChange={(e) => {
            updateManageMatterRequest({
              ...matterRequest,
              subject: e.target.value,
              isSubjectSetByUser: true,
            } as unknown as IMatterRequest);
          }}
          required={true}
          maxLength={250}
          placeholder={t('common:addSubject')}
        />
      </FormItem>
      <FormItem label={t('task:description')}>
        <TextAreaField
          data-testid="description-input"
          value={matterRequest?.description ?? ''}
          onChange={(e) => {
            updateManageMatterRequest({
              ...matterRequest,
              description: e.target.value,
            } as unknown as IMatterRequest);
          }}
          maxLength={1000}
          placeholder={t('common:addDescription')}
        />
      </FormItem>

      {isFromMailOrAppointment && !isAppointment && (
        <FormItem>
          <div className="d-flex justify-between items-center w-full billable-switch">
            <Heading level={6} $color="title" $fontsize="12px" $weight={400} $lineheight="18px">
              {t('transaction:addThisMailAsNote')}
            </Heading>
            <Switch
              data-testid="mail-as-note-switch"
              aria-label={t('transaction:addThisMailAsNote')}
              value={matterRequest?.mailAsNote}
              onChange={(e) => {
                handleSwitchMailAsNote(e);
              }}
            />
          </div>
          {matterRequest?.mailAsNotewithoutAttachmentsWarning && (
            <InfoMessage
              data-testid="mail-attachments-not-saved-note"
              text={t('transaction:mailAttachmentsNotSavedNote')}
              type="warning"
            />
          )}
        </FormItem>
      )}
      <FormItem label={t('matterRequest:type')} name="matterRequestType">
        <div className="relative">
          <MatterRequestTypeSelect
            onOpenPicker={handleSelectMatterRequestType}
            onClear={() =>
              updateManageMatterRequest({
                ...matterRequest,
                matterRequestTypeId: undefined,
                matterRequestType: undefined,
                isMatterTypeSetByUser: true,
              } as unknown as IMatterRequest)
            }
          />
        </div>
      </FormItem>
      <FormItem label={t('common:language')} name="language">
        <div className="relative">
          <MatterRequestLanguageSelect
            onOpenPicker={handleSelectLanguage}
            onClear={() =>
              updateManageMatterRequest({
                ...matterRequest,
                language: undefined,
                isLanguageSetByUser: true,
              } as unknown as IMatterRequest)
            }
          />
        </div>
      </FormItem>
      <FormItem label={t('matterRequest:currency')} name="currency">
        <div className="relative">
          <MatterRequestCurrencySelect
            onOpenPicker={handleSelectCurrency}
            onClear={() =>
              updateManageMatterRequest({
                ...matterRequest,
                currency: undefined,
                isCurrencySetByUser: true,
              } as unknown as IMatterRequest)
            }
          />
        </div>
      </FormItem>
      <FormItem
        label={renderRecommendedLabel(t('matter:owner'))}
        name="owner"
        className={isPeopleReadOnly ? 'disabled' : undefined}
      >
        <div className="relative">
          <MatterRequestUserSelect
            field="owner"
            onOpenPicker={handleSelectOwner}
            disabled={isPeopleReadOnly}
            onClear={() =>
              updateManageMatterRequest({
                ...matterRequest,
                ownerUserId: undefined,
                ownerUser: undefined,
                isOwnerSetByUser: true,
              } as unknown as IMatterRequest)
            }
          />
        </div>
      </FormItem>
      <FormItem
        label={renderRecommendedLabel(t('matterRequest:matterResponsible'))}
        name="responsible"
        className={isPeopleReadOnly ? 'disabled' : undefined}
      >
        <div className="relative">
          <MatterRequestUserSelect
            field="responsible"
            onOpenPicker={handleSelectResponsible}
            disabled={isPeopleReadOnly}
            onClear={() =>
              updateManageMatterRequest({
                ...matterRequest,
                responsibleUserId: undefined,
                responsibleUser: undefined,
                isResponsibleSetByUser: true,
              } as unknown as IMatterRequest)
            }
          />
        </div>
      </FormItem>
      <FormItem label={t('matter:businessArea')} name="businessArea">
        <div className="relative">
          <MatterRequestBusinessAreaSelect
            onOpenPicker={handleSelectBusinessArea}
            onClear={() =>
              updateManageMatterRequest({
                ...matterRequest,
                businessAreaId: undefined,
                businessArea: undefined,
                isBusinessAreaSetByUser: true,
              } as unknown as IMatterRequest)
            }
          />
        </div>
      </FormItem>
      <PinnedSaveButton
        data-testid="create-button"
        disabled={isFormDisabled}
        onClick={isEditing ? onEditMatterRequest : onCreateMatterRequest}
      >
        {isEditing ? t('matterRequest:editMatterRequest') : t('matterRequest:createMatterRequest')}
      </PinnedSaveButton>
    </>
  );
};

export default DetailsForm;
